import * as React from 'react'
import { useLocation } from 'react-router-dom'

import { Box, List } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import { CHANNEL_ORDER } from 'modules/notifications/components/Preferences/PreferencesList/_Category/constants'
import { PreferenceCheck } from 'modules/notifications/components/Preferences/PreferencesList/_PreferenceCheck'
import * as styles from 'modules/notifications/components/Preferences/PreferencesList/_PreferencesItem/index.vanilla.css'
import { ScopeSelector } from 'modules/notifications/components/Preferences/PreferencesList/_ScopeSelector'
import type { NotifierPreferences } from 'modules/notifications/components/Preferences/PreferencesList/hooks/useComputedPreferences'
import type UserPreference from 'modules/notifications/store/models/UserPreference'

interface Props {
  notifier: NotifierPreferences
  onPreferenceUpdated: (preference: UserPreference, newValue: boolean) => Promise<void>
  onScopeUpdated: (notifier: string, newValue: string) => Promise<void>
}

export const PreferencesItem = observer(
  ({ notifier, onPreferenceUpdated, onScopeUpdated }: Props) => {
    // True if the notifier has the focus. Persisted to keep the CSS animation running
    const [focused, setFocused] = React.useState(false)
    const elementRef = React.useRef<HTMLDivElement>(null)
    const { hash } = useLocation()

    // Scroll to this element if the hash matches the notifier
    useUnsafeEffect(() => {
      if (hash === `#notifier__${notifier.identifier}`) {
        setFocused(true)

        // Clear the hash to avoid more scrolling on filtering or navigating
        history.pushState(null, '', '#')

        const timeout = setTimeout(() => {
          elementRef.current?.scrollIntoView({ behavior: 'instant', block: 'center' })
        }, 200)

        return () => {
          clearTimeout(timeout)
        }
      }
    }, [hash, notifier])

    return (
      <List.Item className={focused ? styles.focused : undefined}>
        <Box ref={elementRef} width="full" flexDirection="row" gap="s24" alignItems="center">
          <Box flexGrow flexDirection="column" gap="s4">
            <List.Title>{notifier.title}</List.Title>
            <List.Description>{notifier.description}</List.Description>
            <ScopeSelector notifier={notifier} onScopeUpdated={onScopeUpdated} />
          </Box>
          {CHANNEL_ORDER.map((channel) => {
            const channelData = notifier.channelData[channel]
            const preference = notifier.channelPreferences[channel]

            return (
              <PreferenceCheck
                key={channel}
                channelData={channelData}
                preference={preference}
                onPreferenceUpdated={onPreferenceUpdated}
              />
            )
          })}
        </Box>
      </List.Item>
    )
  }
)
